/**
 * ClientToolsRegistry - Manages client-side tool handlers for VoiceSDK
 * 
 * Tools are defined in the database, but handlers are registered in the frontend.
 * When the backend sends a client_tool_call message, the registry executes
 * the registered handler and sends back the result or error.
 */
class ClientToolsRegistry {

  static MAX_RESULT_SIZE = 50_000;       // 50KB for text-only results
  static MAX_IMAGE_RESULT_SIZE = 512_000; // 500KB for results containing images (base64)

  constructor(sendMessage, getConversationId = () => null, sharedHandlers = null) {
    /**
     * Map of tool name -> handler function.
     * If sharedHandlers is provided, both this registry and any other registry
     * passed the same Map will see the same handlers — used by TTPChatWidget so
     * voice and text SDKs share one registration site (see widget docs).
     * @type {Map<string, Function>}
     */
    this.handlers = sharedHandlers instanceof Map ? sharedHandlers : new Map();

    /**
     * Function to send WebSocket messages
     * @type {Function}
     */
    this.sendMessage = sendMessage;

    /**
     * Function to get current conversation ID
     * @type {Function}
     */
    this.getConversationId = getConversationId;
  }

  /**
   * Register a handler for a client tool
   * @param {string} name - Tool name (must match database definition)
   * @param {Function} handler - Async function that receives parameters and returns result
   * @example
   * registry.registerHandler('getPageContent', async (params) => {
   *   return { title: document.title, content: document.body.innerText };
   * });
   */
  registerHandler(name, handler) {
    if (typeof handler !== 'function') {
      throw new Error(`Handler for tool '${name}' must be a function`);
    }
    this.handlers.set(name, handler);
    console.log(`🔧 ClientTools: Registered handler for '${name}'`);
  }

  /**
   * Unregister a handler
   * @param {string} name - Tool name to unregister
   */
  unregisterHandler(name) {
    this.handlers.delete(name);
    console.log(`🔧 ClientTools: Unregistered handler for '${name}'`);
  }

  /**
   * Check if a handler is registered
   * @param {string} name - Tool name
   * @returns {boolean}
   */
  hasHandler(name) {
    return this.handlers.has(name);
  }

  /**
   * Get all registered handler names
   * @returns {string[]}
   */
  getRegisteredTools() {
    return Array.from(this.handlers.keys());
  }

  /**
   * Handle incoming client_tool_call message from backend
   * @param {Object} message - The tool call message
   * @param {string} message.toolCallId - Unique ID for this call
   * @param {string} message.toolName - Name of the tool to execute
   * @param {Object} message.parameters - Parameters for the tool
   */
  async handleToolCall(message) {
    const { toolCallId, toolName, parameters } = message;
    const startTime = Date.now();
    const receiveTimestamp = new Date().toISOString();

    console.log(`🔧 ClientTools: [${receiveTimestamp}] Received tool call '${toolName}' (id: ${toolCallId})`);
    console.log(`   📋 Full message received:`, JSON.stringify(message, null, 2));
    console.log(`   ⏱️  Start time: ${startTime}ms (${new Date(startTime).toISOString()})`);

    // Check if handler exists
    const handler = this.handlers.get(toolName);
    if (!handler) {
      const registeredTools = Array.from(this.handlers.keys());
      console.error(`🔧 ClientTools: No handler registered for '${toolName}'`);
      console.error(`   📋 Currently registered tools:`, registeredTools);
      console.error(`   📋 Total registered: ${registeredTools.length}`);
      this.sendError(toolCallId, toolName, `No handler registered for tool '${toolName}'`, 'NO_HANDLER', startTime);
      return;
    }

    // Execute handler
    try {
      const result = await handler(parameters);
      const executionTimeMs = Date.now() - startTime;
      
      console.log(`🔧 ClientTools: Tool '${toolName}' completed in ${executionTimeMs}ms`);
      
      this.sendResult(toolCallId, toolName, result, executionTimeMs);
    } catch (error) {
      const executionTimeMs = Date.now() - startTime;
      console.error(`🔧 ClientTools: Tool '${toolName}' failed:`, error);
      
      // Determine error code based on error type
      let errorCode = 'EXECUTION_ERROR';
      if (error.name === 'NotAllowedError' || error.message?.includes('permission')) {
        errorCode = 'PERMISSION_DENIED';
      } else if (error.name === 'NotSupportedError' || error.message?.includes('not supported')) {
        errorCode = 'API_UNAVAILABLE';
      }
      
      this.sendError(toolCallId, toolName, error.message || 'Unknown error', errorCode, executionTimeMs);
    }
  }

  /**
   * Send successful result back to backend
   * @private
   */
  sendResult(toolCallId, toolName, result, executionTimeMs) {
    const conversationId = this.getConversationId();
    const timestamp = new Date().toISOString();

    // Size validation — images (base64) get a higher limit than text-only results
    const hasImage = result && typeof result === 'object' && result.image && result.mimeType;
    const maxSize = hasImage
      ? ClientToolsRegistry.MAX_IMAGE_RESULT_SIZE
      : ClientToolsRegistry.MAX_RESULT_SIZE;

    let serializedSize = 0;
    try {
      serializedSize = JSON.stringify(result).length;
    } catch {
      serializedSize = 0;
    }

    if (serializedSize > maxSize) {
      const sizeKB = Math.round(serializedSize / 1024);
      const limitKB = Math.round(maxSize / 1024);
      console.error(`🔧 ClientTools: Result for '${toolName}' exceeds size limit (${sizeKB}KB > ${limitKB}KB)`);
      this.sendError(toolCallId, toolName,
        `Result too large: ${sizeKB}KB exceeds ${limitKB}KB limit`,
        'RESULT_TOO_LARGE', executionTimeMs);
      return;
    }

    const message = {
      t: 'client_tool_result',
      toolCallId,
      toolName,
      conversationId: conversationId,
      result,
      executionTimeMs
    };
    console.log(`🔧 ClientTools: [${timestamp}] Sending result for tool '${toolName}' (${Math.round(serializedSize / 1024)}KB)`);
    console.log(`   📋 toolCallId: ${toolCallId}`);
    console.log(`   📋 conversationId: ${conversationId || 'null'}`);
    console.log(`   📋 toolName: ${toolName}`);
    this.sendMessage(message);
    console.log(`✅ ClientTools: Result message sent at ${timestamp}`);
  }

  /**
   * Send error back to backend
   * @private
   */
  sendError(toolCallId, toolName, error, errorCode, startTime) {
    const executionTimeMs = typeof startTime === 'number' ? Date.now() - startTime : startTime;
    const timestamp = new Date().toISOString();
    const conversationId = this.getConversationId();
    const message = {
      t: 'client_tool_error',
      toolCallId,
      toolName,
      conversationId: conversationId,
      error,
      errorCode,
      executionTimeMs
    };
    console.log(`🔧 ClientTools: [${timestamp}] Sending error for tool '${toolName}'`);
    console.log(`   📋 toolCallId: ${toolCallId}`);
    console.log(`   📋 conversationId: ${conversationId || 'null'}`);
    console.log(`   📋 toolName: ${toolName}`);
    console.log(`   📋 error: ${error}, errorCode: ${errorCode}`);
    console.log(`   📋 Full message:`, JSON.stringify(message, null, 2));
    this.sendMessage(message);
    console.log(`✅ ClientTools: Error message sent at ${timestamp}`);
  }

  /**
   * Update the sendMessage function (called when WebSocket reconnects)
   * @param {Function} sendMessage - New send function
   */
  setSendMessage(sendMessage) {
    this.sendMessage = sendMessage;
  }
}

export default ClientToolsRegistry;

